import { sleepAsync } from '../libs/promise';

export default class ApiClient {
    constructor(
        protected readonly api_path: string,
        readonly timeout: number = 15000
    ) {}

    private timeoutAbortSignal(ms?: number): AbortSignal | undefined {
        if (!('AbortController' in self)) return undefined;
        const aborter = new AbortController();
        self.addEventListener('unload', () => aborter.abort());
        setTimeout(() => aborter.abort(), ms ?? this.timeout);
        return aborter.signal;
    }

    async post(
        path: string,
        obj: { [k: string]: any } = {},
        timeoutMs?: number
    ): Promise<any> {
        try {
            const res = await fetch(this.api_path + path, {
                method: 'POST',
                body: JSON.stringify(obj),
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal: this.timeoutAbortSignal(timeoutMs),
            });
            this._onLine = true;

            const json = await res.json();
            if (json.error) console.error('API Error.', json);
            return json;
        } catch (err: any) {
            if (this.isConnectionError(err)) this._onLine = false;
            if (err?.name === 'AbortError') throw new Error('request_timeout');
            throw err;
        }
    }

    async put(
        path: string,
        content: string | Blob,
        param: { [k: string]: any } = {},
        timeoutMs?: number
    ): Promise<any> {
        try {
            const res = await fetch(this.api_path + path, {
                method: 'PUT',
                body: content,
                headers: {
                    Accept: 'application/json',
                    Parameter: JSON.stringify(param),
                },
                credentials: 'include',
                signal: this.timeoutAbortSignal(timeoutMs),
            });
            this._onLine = true;

            const json = await res.json();
            if (json.error) console.error('API Error.', json);
            return json;
        } catch (err: any) {
            if (this.isConnectionError(err)) this._onLine = false;
            if (err?.name === 'AbortError') throw new Error('request_timeout');
            throw err;
        }
    }

    isConnectionError(err: Error): boolean {
        return (
            err.name == 'TypeError' && err.message.includes('Failed to fetch')
        );
    }

    private _onLine: boolean = true;
    get onLine(): boolean {
        return this._onLine;
    }
    private _checkOnLine?: Promise<boolean>;
    async checkOnLine(): Promise<boolean> {
        return (this._checkOnLine ??= (async () => {
            try {
                await fetch(this.api_path, {
                    cache: 'no-store',
                    signal: this.timeoutAbortSignal(),
                });
                return (this._onLine = true);
            } catch (err) {
                console.error('check before reload.', err);
                return (this._onLine = false);
            } finally {
                delete this._checkOnLine;
            }
        })());
    }

    private _waitForOnline?: Promise<void>;
    waitForOnline(): Promise<void> {
        return (this._waitForOnline ??= (async () => {
            try {
                while (!(await this.checkOnLine())) {
                    await sleepAsync(this.timeout);
                }
            } finally {
                delete this._waitForOnline;
            }
        })());
    }
}
